/*
 *    The school screens' language (mirinae-mono#53): Korean or English, for the school product's own
 *    screens only -- the whole admin app (its school section and, since 2026-10-15, the staff pages too),
 *    the university app (sign-in, invitation, teacher and student pages) and the class card in Lessons.
 *
 *    It is NOT mirinae's screen language. mirinae also uses that one as the content language (analyzer
 *    translations, grammar notes, lesson lists), and it has no Korean: putting `ko` into the shared
 *    Localization made Korean browsers ask for Korean lessons and stopped the lessons app. So nothing here
 *    calls Localization.setLanguage, touches `preferences.language` or decides which language a lesson or
 *    an API call uses; it only picks the words on a school screen.
 *
 *    Which language: the one the person chose on a school screen (kept in this browser), else Korean when
 *    the browser's first language is Korean, else English. A key with no Korean shows its English.
 */
import { useEffect, useState } from 'react';
import moment from 'moment';
import 'moment/locale/ko';
import koKR from 'antd/locale/ko_KR';
import enUS from 'antd/locale/en_US';

import KO from './ko.json';

// Loading a moment locale makes it the global one; this puts the global back to English (a change of its
// own, once, when this module loads), so only school dates are Korean.
moment.locale('en');

const STORE_KEY = 'mirinae-school-ui-language';
const LANGUAGES = ['ko', 'en'];

const stored = () => {
    try { return localStorage.getItem(STORE_KEY); } catch (e) { return null; }
};

// the browser's first choice only: Korean somewhere down its list is not a preference for Korean
const browserLanguage = () => {
    if (typeof navigator === 'undefined') return 'en';
    const first = (navigator.languages && navigator.languages[0]) || navigator.language || '';
    return /^ko(-|$)/i.test(first) ? 'ko' : 'en';
};

let chosen = LANGUAGES.includes(stored()) ? stored() : null;
const listeners = new Set();

export const schoolLanguage = () => chosen || browserLanguage();

// Kept for this browser; when storage is not available it lasts until the page is reloaded.
export const setSchoolLanguage = (language) => {
    if (!LANGUAGES.includes(language)) return;
    chosen = language;
    try { localStorage.setItem(STORE_KEY, language); } catch (e) { /* this page only */ }
    listeners.forEach(listener => listener(language));
};

// for a component that has to show the change at once
export const useSchoolLanguage = () => {
    const [language, setLanguage] = useState(schoolLanguage);
    useEffect(() => {
        listeners.add(setLanguage);
        return () => { listeners.delete(setLanguage); };
    }, []);
    return language;
};

/**
 * The words of one school screen: `t(key, english, values)`. Korean from `ko.json` when the school
 * language is Korean and it has the key, otherwise the English given here -- not mirinae's Localization,
 * whose language is a different one (the school screens are Korean or English only).
 * `values` fills `{name}`-style places; a screen that fills them itself leaves it out, as before.
 * A component showing these words subscribes with useSchoolLanguage() to follow the switch.
 */
export const schoolText = (prefix) => (key, english, values) => {
    const full = `${prefix}${key}`;
    const text = (schoolLanguage() === 'ko' && KO[full]) || english;
    return values
        ? Object.entries(values).reduce((s, [k, v]) => s.split(`{${k}}`).join(v), text)
        : text;
};

// for date formatting and Ant Design on school screens
export const schoolLocale = () => (schoolLanguage() === 'ko' ? 'ko-KR' : 'en-US');
export const schoolAntdLocale = () => (schoolLanguage() === 'ko' ? koKR : enUS);
export const schoolMoment = (date) => moment(date).locale(schoolLanguage() === 'ko' ? 'ko' : 'en');
